import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import moment from "moment";
import React from "react";

import { classNames } from "../../../helpers";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForUsers } from "../../../utils/confighelpers";
import { useSelector } from "react-redux";
import { userState } from "../../../redux/reducers/userReducer";
import { clubSinceDate, euro, fetchBuckets, InfoTip, KPI_ICONS, revenueScopeText } from "./shared";

/**
 * Members / Bookings / Revenue tiles, as in the original dashboard:
 * the big number is the all-time total, and next to it "+N <period> ▾" with a period menu in each tile
 * (today / this week / this month).
 *
 * Numbers: GET users/statistics?listFor=class (the same request the original StatBox tiles used):
 *  - members.totalMembers, todayMembers, weekMembers, monthMembers                       (members who joined)
 *  - bookings.totalBookings, todayBookings, weekBookings, monthBookings                  (bookings made)
 *  - bookingAndPurchaseRevenue.totalRevenue, todayRevenue, weekRevenue, monthRevenue     (paid online)
 *  The backend counts "this week" from Sunday and everything by created date (UTC).
 * Small chart (top right), follows the tile's period: last 8 days / 8 weeks / 6 months
 * (allMembersStats, allBookingStats status=Confirmed, allRevenueStats).
 */

export const PERIODS = {
  today: { label: "today" },
  week: { label: "this week" },
  month: { label: "this month" },
};

// Tile colours = the darker side of the original FitClub stat boxes. The icon square uses the same colour;
// a selected tile is filled with it, with white text.
const TILE = {
  members: { title: "Members", fill: "#552A4A", spark: "#552A4A", stats: "members", total: "totalMembers", prefix: "Members" },
  bookings: { title: "Bookings", fill: "#E45B64", spark: "#E45B64", stats: "bookings", total: "totalBookings", prefix: "Bookings" },
  revenue: { title: "Revenue", fill: "#B8ABCA", spark: "#9886AC", stats: "bookingAndPurchaseRevenue", total: "totalRevenue", prefix: "Revenue" },
};

/** All-time totals and today / week / month figures for the three tiles. */
export function useKpis() {
  const [stats, setStats] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axiosInstance({ method: "get", url: `${getUrlForUsers()}statistics?listFor=class` });
        if (alive) setStats(res?.status === 200 && res.data?.statusCode === 200 ? res.data.data || {} : {});
      } catch (e) {
        if (alive) setStats({});
      }
    })();
    return () => {
      alive = false;
    };
  }, []);
  return stats;
}

const SPARK = {
  members: (period) => {
    const back = { today: ["daily", 8], week: ["weekly", 8], month: ["monthly", 6] }[period];
    return ["allMembersStats", { interval: back[0], size: back[1], startDate: moment() }];
  },
  revenue: (period) => {
    const back = { today: ["daily", 8], week: ["weekly", 8], month: ["monthly", 6] }[period];
    return ["allRevenueStats", { interval: back[0], size: back[1], startDate: moment() }];
  },
  bookings: (period) => {
    const fwd = { today: ["daily", 8, moment().subtract(7, "days")], week: ["weekly", 8, moment().subtract(7, "weeks")], month: ["monthly", 6, moment().subtract(5, "months")] }[period];
    return ["allBookingStats", { interval: fwd[0], size: fwd[1], startDate: fwd[2], extra: "&status=Confirmed" }];
  },
};

function useSpark(key, period) {
  const [values, setValues] = React.useState([]);
  React.useEffect(() => {
    let alive = true;
    const [endpoint, opts] = SPARK[key](period);
    fetchBuckets(endpoint, opts)
      .then((b) => alive && setValues(b.map((x) => parseFloat(x.count) || 0)))
      .catch(() => alive && setValues([]));
    return () => {
      alive = false;
    };
  }, [key, period]);
  return values;
}

function Spark({ values, color }) {
  if (!values?.length) return null;
  const max = Math.max(...values, 1);
  return (
    <span className="flex h-8 items-end gap-[3px]" aria-hidden="true">
      {values.map((v, i) => (
        <span key={i} className="w-2 rounded-t-[2px]" style={{ height: `${Math.max(8, (v / max) * 100)}%`, background: color, opacity: i === values.length - 1 ? 1 : 0.35 }} />
      ))}
    </span>
  );
}

const storedPeriod = (key) => {
  try {
    const v = window.localStorage.getItem(`fc.dashboard.period.${key}`);
    return PERIODS[v] ? v : "week";
  } catch (e) {
    return "week";
  }
};

function KpiTile({ tileKey, stats, active, onOpen }) {
  const cfg = TILE[tileKey];
  const [period, setPeriod] = React.useState(() => storedPeriod(tileKey));
  const spark = useSpark(tileKey, period);
  const data = stats ? stats[cfg.stats] || {} : null;
  const isMoney = tileKey === "revenue";
  const fmt = (n) => (isMoney ? euro(n) : (parseFloat(n) || 0).toLocaleString("nl-NL"));
  const total = data ? fmt(data[cfg.total]) : null;
  const added = data ? data[`${period}${cfg.prefix}`] : null;
  const addedText = added == null ? "" : isMoney ? `+ ${euro(added)}` : `+${(parseFloat(added) || 0).toLocaleString("nl-NL")}`;
  const on = active === tileKey;
  const since = clubSinceDate(useSelector(userState).userData);

  const choose = (p) => {
    setPeriod(p);
    try {
      window.localStorage.setItem(`fc.dashboard.period.${tileKey}`, p);
    } catch (e) {
      /* not stored */
    }
  };

  return (
    <div
      style={on ? { background: cfg.fill, borderColor: cfg.fill, boxShadow: `0 12px 28px ${cfg.fill}40` } : undefined}
      className={classNames(
        "relative flex cursor-pointer flex-col gap-4 rounded-[20px] border p-5 transition",
        on ? "text-white" : "border-gray-200 bg-white shadow-[0_1px_2px_rgba(29,19,32,0.04)] hover:border-[#D9C2F2] hover:shadow-[0_8px_24px_rgba(126,0,221,0.08)]"
      )}
    >
      {/* The whole tile is one button (stretched over it); the period menu and the info icon sit above it. */}
      <button
        type="button"
        onClick={() => onOpen(tileKey)}
        aria-pressed={on}
        aria-label={on ? `${cfg.title} insights shown. Back to the overview` : `Show ${cfg.title.toLowerCase()} insights`}
        className="absolute inset-0 rounded-[20px] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#D9C2F2]"
      />
      <div className="pointer-events-none relative flex w-full items-center gap-2.5">
        <span
          style={{ background: cfg.fill }}
          className={classNames("flex shrink-0 items-center justify-center text-white", on ? "-ml-1.5 h-12 w-12 rounded-2xl [&>svg]:h-7 [&>svg]:w-7" : "h-9 w-9 rounded-xl")}
        >
          {KPI_ICONS[tileKey]}
        </span>
        <span className={classNames("flex flex-1 items-center gap-1 font-extrabold", on ? "text-xl text-white" : "text-[15px] text-gray-700")}>
          {cfg.title}
          {tileKey === "revenue" && (
            <span className="pointer-events-auto">
              <InfoTip label="What revenue includes" text={revenueScopeText(since)} light={on} />
            </span>
          )}
        </span>
        <Spark values={spark} color={on ? "#FFFFFF" : cfg.spark} />
      </div>
      <div className="pointer-events-none relative flex w-full items-end justify-between gap-3">
        <div className="min-w-0">
          {total === null ? (
            <span className={classNames("block h-9 w-28 animate-pulse rounded-xl", on ? "bg-white/25" : "bg-[#EDE5F0]")} />
          ) : (
            <span className={classNames("block font-black leading-none tracking-tight", on ? "text-[44px] text-white" : "text-[34px] text-gray-900")}>{total}</span>
          )}
          <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
            {data && <span className={classNames("font-black", on ? "text-lg text-white" : parseFloat(added) > 0 ? "text-[15px] text-[#1F6B3A]" : "text-[15px] text-gray-400")}>{addedText}</span>}
            <Menu as="div" className="pointer-events-auto relative">
              <MenuButton
                aria-label={`${cfg.title}: period`}
                className={classNames(
                  "inline-flex items-center gap-1 rounded-lg px-1.5 font-bold",
                  on ? "h-8 text-[15px] text-white hover:bg-white/15 data-[open]:bg-white/15" : "h-7 text-[13px] " + "text-gray-500 hover:bg-[#F7F4F8] hover:text-gray-900 data-[open]:bg-[#F7F4F8] data-[open]:text-gray-900"
                )}
              >
                {PERIODS[period].label}
                <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
                  <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
                </svg>
              </MenuButton>
              <MenuItems anchor="bottom start" className="z-30 mt-1 w-40 rounded-xl border border-gray-200 bg-white p-1 shadow-lg focus:outline-none">
                {Object.entries(PERIODS).map(([k, p]) => (
                  <MenuItem key={k}>
                    <button
                      type="button"
                      onClick={() => choose(k)}
                      className={classNames(
                        "flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm font-bold data-[focus]:bg-[#F7F4F8]",
                        k === period ? "text-indigo-bz" : "text-gray-900"
                      )}
                    >
                      {p.label.charAt(0).toUpperCase() + p.label.slice(1)}
                      {k === period && (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true">
                          <path d="m5 12 5 5L20 7" />
                        </svg>
                      )}
                    </button>
                  </MenuItem>
                ))}
              </MenuItems>
            </Menu>
          </div>
        </div>
      </div>
    </div>
  );
}

export function KpiTiles({ kpis, active, onOpen }) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      {Object.keys(TILE).map((k) => (
        <KpiTile key={k} tileKey={k} stats={kpis} active={active} onOpen={onOpen} />
      ))}
    </div>
  );
}
